import { CancelButton, SaveButton } from '@core/components/Buttons';
import { FormDialogContent, SimpleDialog } from '@core/components/Dialog';
import { useApiUtils } from '@core/util/apiUtils';
import {
  useClienteInitialValue,
  useHabilitarDescontoInitialValue,
  useHabilitarFreteInitialValue,
  useHabilitarTipoDescontoInitialValue,
  useProdutosServicosInitialValue,
  useVendaInitialValue,
} from 'app/main/venda/components/modal/VendaInitialValues';
import { cancelarAlert, confirmarAlert } from 'app/services/alerts';
import api from 'app/services/api/notaRemessaApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { eventReactGA } from 'app/utils/analyticsUtil';
import { FormikProvider, useFormik } from 'formik';
import React, { useContext, useEffect } from 'react';
import { FaTags } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { NotaFiscalContext } from '../../NotaFiscalContext';
import NotaRemessaForm from './NotaRemessaForm';
import schemaNotaRemessa from './SchemaNotaRemessa';

const NotaFiscalRemessaModal = () => {
  const [modal, setModal] = useContext(NotaFiscalContext).modal;
  const [notaAtual, setNotaAtual] = useContext(NotaFiscalContext).notaRemessa;
  const [, setLoading] = useContext(NotaFiscalContext).loading;
  const [, setPendencias] = useContext(GlobalContext).pendencias;
  const [, setModalPendenciasFaturamento] = useContext(GlobalContext).modalPendenciasFaturamento;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useEffect(() => {
    formik.setValues(initialValues);
    // Reset controlado pelo gatilho acima; incluir o formulário/valores reiniciaria o
    // preenchimento enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notaAtual]);

  const initialValues = {
    id: notaAtual?.id || null,
    destinatario: useClienteInitialValue(notaAtual?.destinatario),
    venda: useVendaInitialValue(notaAtual?.venda),
    produtosServicos: useProdutosServicosInitialValue(notaAtual?.produtosServicos),
    operacao: notaAtual?.operacao || '',
    previsaoEntrega: notaAtual?.previsaoEntrega,
    motivoRemessa: notaAtual?.motivoRemessa || '',
    valorFrete: notaAtual?.valorFrete || '',
    tipoDesconto: notaAtual?.tipoDesconto || ` `,
    valorDesconto: notaAtual?.valorDesconto || '',
    valorTotal: notaAtual?.valorTotal || '',
    responsavelFrete: notaAtual?.responsavelFrete || ' ',
    transportadora: notaAtual?.transportadora,
    quantidadeVolumeTransportado: notaAtual?.quantidadeVolumeTransportado || '',
    tipoVolumeTrasportado: notaAtual?.tipoVolumeTrasportado || '',
    pesoBruto: notaAtual?.pesoBruto || '',
    pesoLiquido: notaAtual?.pesoLiquido || '',
    habilitarFrete: useHabilitarFreteInitialValue(notaAtual?.produtosServicos),
    habilitarTipoDesconto: useHabilitarTipoDescontoInitialValue(notaAtual?.produtosServicos),
    habilitarDesconto: useHabilitarDescontoInitialValue(notaAtual?.tipoDesconto),
    informacoesComplementares: notaAtual?.informacoesComplementares || '',
  };

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const trataDadosFiscaisProduto = (fiscal) => {
    if (!fiscal) {
      return null;
    }

    const aliquotaAtualizada = JSON.parse(JSON.stringify(fiscal));

    if (!aliquotaAtualizada.id) {
      aliquotaAtualizada.id = null;
    }

    if (aliquotaAtualizada.tipoUnidadeMedida === 'VAZIO') {
      aliquotaAtualizada.tipoUnidadeMedida = 'UNIDADE';
    }

    if (fiscal?.codigoNCM?.id) {
      aliquotaAtualizada.codigoNCM = fiscal.codigoNCM.id;
    } else {
      aliquotaAtualizada.codigoNCM = null;
    }

    if (fiscal?.cest?.id) {
      aliquotaAtualizada.cest = fiscal.cest.id;
    } else {
      aliquotaAtualizada.cest = null;
    }

    if (fiscal?.cfop?.id) {
      aliquotaAtualizada.cfop = fiscal.cfop.id;
    } else {
      aliquotaAtualizada.cfop = null;
    }

    if (fiscal?.origemProduto?.id) {
      aliquotaAtualizada.origemProduto = fiscal.origemProduto.id;
    } else {
      aliquotaAtualizada.origemProduto = null;
    }

    if (fiscal?.ipi?.situacaoTributaria?.id) {
      aliquotaAtualizada.ipi.situacaoTributaria = fiscal.ipi.situacaoTributaria.id;
    } else {
      aliquotaAtualizada.ipi = null;
    }

    if (fiscal?.icms?.situacaoTributaria?.id) {
      aliquotaAtualizada.icms.situacaoTributaria = fiscal.icms.situacaoTributaria?.id;
    } else {
      aliquotaAtualizada.icms = null;
    }

    if (aliquotaAtualizada.icms) {
      if (fiscal?.icms?.motivoDesoneracao?.id) {
        aliquotaAtualizada.icms.motivoDesoneracao = fiscal.icms.motivoDesoneracao.id;
      } else {
        aliquotaAtualizada.icms.motivoDesoneracao = null;
      }

      if (fiscal?.icms?.ufIcmsStDevido?.id) {
        aliquotaAtualizada.icms.ufIcmsStDevido = fiscal.icms.ufIcmsStDevido.id;
      } else {
        aliquotaAtualizada.icms.ufIcmsStDevido = null;
      }
    }

    return aliquotaAtualizada;
  };

  const montaEntidadeParaEnvio = (nota) => {
    const request = JSON.parse(JSON.stringify(nota));

    request.destinatarioId = nota.destinatario?.id;
    request.vendaId = nota.venda?.id;
    request.transportadoraId = nota.transportadora?.id;

    if (request.produtosServicos)
      request.produtosServicos = request.produtosServicos.map(
        ({ id, fiscal, produtoServico: { produtoId, servicoId, issRetido, imobilizado }, quantidade, valor }) => ({
          id,
          produtoId,
          servicoId,
          quantidade,
          valor,
          issRetido,
          imobilizado,
          fiscal: trataDadosFiscaisProduto(fiscal),
        })
      );

    return request;
  };

  const submitForm = async (nota) => {
    validarPendencias();
  };

  const validarPendencias = async () => {
    if (!formik?.dirty) {
      return;
    }

    setLoading(true);

    const entidade = montaEntidadeParaEnvio(formik.values);

    try {
      await api.validarPendencias(entidade).then((response) => {
        if (response.data && response.data.length > 0) {
          setPendencias(response.data);
          setModalPendenciasFaturamento('modalPendenciasFaturamento');
          setLoading(false);
        } else {
          setLoading(false);
          if (notaAtual?.id) {
            salvarRemessa();
          } else {
            emitirNota();
          }
        }
      });
    } catch (err) {
      apiUtils.handleApiError(err);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const salvarRemessa = async () => {
    if (!formik?.dirty) {
      return;
    }

    setLoading(true);

    const entidade = montaEntidadeParaEnvio(formik.values);

    try {
      eventReactGA('Nota fiscal', 'Cadastrar nota fiscal de remessa');
      const response = await api.cadastrar(entidade);
      if (response.data) {
        showSuccess('Nota de remessa salva com sucesso');
        close();
      }
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const emitirNota = async () => {
    confirmarAlert({
      mensagem:
        'Tem certeza que deseja emitir a nota fiscal de remessa? Ao concordar, ela será emitida imediatamente. ',
      confirmar: async () => {
        if (!formik?.dirty) {
          return;
        }

        setLoading(true);

        const entidade = montaEntidadeParaEnvio(formik.values);

        try {
          eventReactGA('Nota fiscal', 'Emitir nota fiscal de remessa');
          const response = await api.cadastrar(entidade);
          if (response.data) {
            showSuccess('Nota de remessa salva com sucesso');
            close();
          }
        } catch (err) {
          apiUtils.handleApiError(err);
        } finally {
          setLoading(false);
          await api.atualizarHomol('CONCLUIDO');
        }
      },
    });
  };

  const formik = useFormik({
    validationSchema: schemaNotaRemessa,
    initialValues,
    onSubmit: submitForm,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const close = () => {
    setNotaAtual({});
    formik.resetForm({});
    setModal('');
  };

  const handleClose = () => {
    if (formik.dirty) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  return (
    <SimpleDialog fullScreen open={modal === 'notaRemessa'} onClose={handleClose} innerScroll>
      <FormikProvider value={formik}>
        <FormDialogContent
          onSubmit={() => {}}
          fullScreen
          header={{
            title: 'Nota de remessa',
            icon: <FaTags />,
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleClose}>
                  Cancelar
                </CancelButton>
                <SaveButton
                  color="buttonSave"
                  type="submit"
                  styles={undefined}
                  onClick={formik.handleSubmit}
                  variant="contained"
                >
                  {notaAtual?.id ? 'Salvar' : 'Emitir Nota Fiscal'}
                </SaveButton>
              </>
            ),
          }}
          content={
            <div className="h-full max-h-full w-full">
              <NotaRemessaForm />
            </div>
          }
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default NotaFiscalRemessaModal;
